import {MatButton} from '@angular/material/button';
import {Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild, NgZone} from '@angular/core';
import {Subject, BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map, take, takeUntil} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import {TorchStatsService} from '@backend/modules/@users/services/torch-stats.service';
import {StockPriceIex} from '@backend/modules/@users/models/stock-price-iex.model';
import {MatDialog} from '@angular/material/dialog';
import {NotificationDialogComponent} from './notification-dialog/notification-dialog.component';
import {MatDialogRef} from '@angular/material/dialog';
import {GlobalNotification} from '@backend/modules/@users/models/global-notification.model';
import {TorchGlobalNotificationsService} from '@backend/modules/@users/services/torch-global-notifications.service';
import {FuseConfigService} from '@fuse/services/config.service';
import {FuseSidebarService} from '@fuse/components/sidebar/sidebar.service';
import {fuseNavigation} from 'app/fuse-navigation';
import {TorchUser} from '@backend/modules/@users/models/torch-user.model';
import {TorchUserService} from '@backend/modules/@users/services/torch-user.service';
import {MsalService} from '@azure/msal-angular';
import {CookieService} from 'ngx-cookie-service';
import {FeedbackDialogService} from '../../../shared/components/feedback-dialog/feedback-dialog.service';
import {SessionService} from '../../../shared/services/session.service';
import {QueryFilterDialogService} from '../../../shared/modules/query-filters/services/query-filter-dialog.service';
import {SoftwareUpdateService} from '../../../shared/services/software-update.service';
import {fuseAnimations} from '../../../../@fuse/animations';

@Component({
  selector: 'toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations
})

export class ToolbarComponent implements OnInit, OnDestroy {
  @ViewChild('submitFeedbackButton') submitFeedbackButton: MatButton;

  horizontalNavbar: boolean;
  rightNavbar: boolean;
  hiddenNavbar: boolean;
  languages: any;
  navigation: any;
  selectedLanguage: any;
  currentUser: Observable<TorchUser>;
  fuseConfig: any;
  darkMode: Observable<string>;
  currentUserAvatar: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  allNotifications: GlobalNotification[];

  stockPrice: StockPriceIex;
  showStockPrice = true;

  private currentDialog: MatDialogRef<NotificationDialogComponent>;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param _fuseConfigService
   * @param _fuseSidebarService
   * @param _translateService
   * @param _usersService
   * @param _msalService
   * @param _cookieService
   * @param _notificationService
   * @param _statsService
   * @param _sessionService
   * @param _ngZone
   * @param dialog
   * @param feedbackDialogService
   * @param queryFilterDialogService
   * @param softwareUpdateService
   */
  constructor(
    private _fuseConfigService: FuseConfigService,
    private _fuseSidebarService: FuseSidebarService,
    private _translateService: TranslateService,
    private _usersService: TorchUserService,
    private _msalService: MsalService,
    private _cookieService: CookieService,
    private _notificationService: TorchGlobalNotificationsService,
    private _statsService: TorchStatsService,
    private _sessionService: SessionService,
    private _ngZone: NgZone,
    public dialog: MatDialog,
    private feedbackDialogService: FeedbackDialogService,
    private queryFilterDialogService: QueryFilterDialogService,
    private softwareUpdateService: SoftwareUpdateService
  ) {
    this.currentUser = this._usersService.currentTorchUser.pipe(
      take(1)
    );

    // Set the defaults
    this.navigation = fuseNavigation;

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this.darkMode = this._sessionService.darkMode.pipe(
      takeUntil(this._unsubscribeAll),
      distinctUntilChanged()
    );

    this._usersService.getSignedInUserAvatar().pipe(
      take(1)
    ).subscribe(avatar => {
      this.currentUserAvatar.next(avatar);
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to the config changes
    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((settings) => {
        this.horizontalNavbar = settings.layout.navbar.position === 'top';
        this.rightNavbar = settings.layout.navbar.position === 'right';
        this.hiddenNavbar = settings.layout.navbar.hidden === true;

        this.fuseConfig = settings;
      });

    this._notificationService.allNotifications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((notifications) => {
        this.allNotifications = notifications;
      });

    this._statsService.latestStockPrice$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((stocks) => {
        this.stockPrice = stocks;
      });

    this._sessionService.toolbarStockPrice
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((show) => {
        this.showStockPrice = show;
      });

    // Set the selected language from default languages
    this.selectedLanguage = _.find(this.languages, {id: this._translateService.currentLang});
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebarOpen(key): void {
    this._fuseSidebarService.getSidebar(key).toggleOpen();
  }

  /**
   * Search
   *
   * @param value
   */
  search(value): void {
    // Do your search here...
    console.log(value);
  }

  logout() {
    this._msalService.logout();
  }

  toggleDarkMode(): void {
    this._sessionService.isChangingThemes.next(true);
    this._sessionService.darkMode.pipe(
      take(1),
      map(colorTheme => {
        let newTheme;
        if (colorTheme === 'theme-phoenix-light') {
          newTheme = 'theme-phoenix-dark';
        } else {
          newTheme = 'theme-phoenix-light';
        }

        return newTheme;
      })
    ).subscribe(colorTheme => {
      this.fuseConfig.colorTheme = colorTheme;
      this._fuseConfigService.config = this.fuseConfig;
      this._sessionService.updateDarkMode(colorTheme);
    });
  }

  public openDialog(notification: GlobalNotification) {
    this.currentDialog = this.dialog.open(NotificationDialogComponent, {
      data: notification,
      panelClass: 'notification-dialog'
    });
  }

  openFeedbackDialog() {
    this.submitFeedbackButton.disabled = true;
    this.feedbackDialogService.openFeedbackDialog().subscribe(completed => {
      this.submitFeedbackButton.disabled = !completed;
    });
  }

  openManageQueryFiltersDialog() {
    this.queryFilterDialogService.manageFilters();
  }

  testSoftwareUpdateAlert() {
    this.softwareUpdateService.promptUserUpdate();
  }

  checkSoftwareUpdate() {
    this.softwareUpdateService.checkForUpdate();
  }

  isDarkMode(theme: string) {
    return theme === 'theme-phoenix-dark';
  }
}
